import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const TestEngineMockPage = () => {
    const { quizId } = useParams();
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQIndex, setCurrentQIndex] = useState(0);

    const [answers, setAnswers] = useState({});
    const [markedForReview, setMarkedForReview] = useState(new Set());
    const [visitedQuestions, setVisitedQuestions] = useState(new Set([0]));

    const [timeLeft, setTimeLeft] = useState(0);
    const [loading, setLoading] = useState(true);

    // Load Quiz
    useEffect(() => {
        api.get(`/test-zone/start-test/${quizId}`).then(res => {
            if (res.data.success) {
                setQuiz(res.data.quiz);
                setQuestions(res.data.questions);
                setTimeLeft((res.data.quiz.MaxTime || 60) * 60);
                setLoading(false);
            }
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    }, [quizId]);

    // Timer
    useEffect(() => {
        if (!timeLeft && timeLeft !== 0) return;
        if (timeLeft <= 0) return;
        const timerId = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) { clearInterval(timerId); submitTest(); return 0; }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timerId);
    }, [timeLeft]);

    // Track visited questions
    useEffect(() => {
        if (questions.length > 0) {
            setVisitedQuestions(prev => new Set(prev).add(currentQIndex));
        }
    }, [currentQIndex, questions]);

    const formatTime = (s) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec < 10 ? '0' + sec : sec}`;
    };

    // ── Actions ────────────────────────────────────────────────────────────

    const handleOptionSelect = (qId, optId) => {
        setAnswers(prev => ({ ...prev, [qId]: optId }));
    };

    const handleSaveNext = () => {
        if (currentQIndex < questions.length - 1) setCurrentQIndex(currentQIndex + 1);
    };

    const handleClear = (qId) => {
        const newAnswers = { ...answers };
        delete newAnswers[qId];
        setAnswers(newAnswers);
        // Also remove from marked if it was only marked-without-answer
        const newMarked = new Set(markedForReview);
        newMarked.delete(qId);
        setMarkedForReview(newMarked);
    };

    // Save answer + mark for review → green dot (ans-marked)
    const handleSaveMarkReview = (qId) => {
        setMarkedForReview(prev => new Set(prev).add(qId));
        // Answer stays as-is (already set via handleOptionSelect)
        if (currentQIndex < questions.length - 1) setCurrentQIndex(currentQIndex + 1);
    };

    // Mark for review WITHOUT saving answer → purple only, no dot (marked)
    const handleMarkReviewNext = (qId) => {
        // Clear any selected answer so status becomes 'marked' not 'ans-marked'
        setAnswers(prev => {
            const next = { ...prev };
            delete next[qId];
            return next;
        });
        setMarkedForReview(prev => new Set(prev).add(qId));
        if (currentQIndex < questions.length - 1) setCurrentQIndex(currentQIndex + 1);
    };

    const submitTest = async () => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("Are you sure you want to finish the test?")) return;
        try {
            const res = await api.post('/test-zone/submit', {
                quizId,
                userAnswers: answers,
                timeTaken: (quiz?.MaxTime * 60) - timeLeft
            });
            if (res.data.success) {
                navigate('/test-result', { state: { result: res.data.data, quiz, userAnswers: answers } });
            }
        } catch (err) {
            console.error("Submission failed", err);
        }
    };

    // ── Status helpers ─────────────────────────────────────────────────────

    const getQuestionStatus = (idx, qId) => {
        const isAnswered = answers[qId] !== undefined;
        const isMarked   = markedForReview.has(qId);
        const isVisited  = visitedQuestions.has(idx);

        // "Save & Mark for Review" → answer kept + marked → green dot
        if (isAnswered && isMarked)  return 'ans-marked';
        // "Mark for Review & Next"  → answer cleared + marked → no dot
        if (isMarked)                return 'marked';
        if (isAnswered)              return 'answered';
        if (isVisited && !isAnswered) return 'not-answered';
        return 'not-visited';
    };

    const getStatusStyle = (status) => {
        const trapeziumClip = "polygon(0 0, 100% 20%, 100% 80%, 0 100%)";
        switch (status) {
            case 'marked':
                return { className: "bg-purple-900 text-white rounded-[50%]", style: {} };
            case 'ans-marked':
                return { className: "bg-purple-600 text-white rounded-[50%] relative", style: {} };
            case 'answered':
                return { className: "bg-green-500 text-white", style: { clipPath: trapeziumClip, width: '40px' } };
            case 'not-answered':
                return { className: "bg-red-500 text-white", style: { clipPath: trapeziumClip, width: '40px' } };
            default:
                return { className: "bg-gray-200 text-gray-700 border border-gray-400 rounded-sm", style: {} };
        }
    };

    // ── Palette (shared for mobile / desktop) ──────────────────────────────

    const PaletteContent = ({ isMobile }) => (
        <div className={`bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-3 ${isMobile ? 'h-full' : 'h-full flex flex-col'}`}>
            <div className="mb-3 pb-2 border-b border-gray-200">
                <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide">Question Palette</h3>
            </div>

            <div className={`flex-1 overflow-auto custom-scrollbar ${isMobile ? 'whitespace-nowrap pb-2' : 'mb-4'}`}>
                <div className={`${isMobile ? 'flex gap-2 px-1' : 'grid grid-cols-4 gap-3 place-items-center'}`}>
                    {questions.map((q, idx) => {
                        const status = getQuestionStatus(idx, q.QuesId);
                        const { className, style } = getStatusStyle(status);
                        const isCurrent = currentQIndex === idx;
                        return (
                            <button
                                key={q.QuesId}
                                onClick={() => setCurrentQIndex(idx)}
                                className={`h-9 min-w-[36px] flex items-center justify-center font-bold text-xs transition-all ${className} ${isCurrent ? 'ring-2 ring-black ring-offset-1 z-10' : ''}`}
                                style={style}
                            >
                                {idx + 1}
                                {/* Green dot only for ans-marked */}
                                {status === 'ans-marked' && (
                                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={`mt-auto pt-3 border-t border-gray-200 ${isMobile ? 'hidden' : 'block'}`}>
                <h4 className="font-bold text-gray-600 text-xs mb-3">LEGENDS</h4>
                <div className="grid grid-cols-1 gap-3 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center">
                            <div className="w-6 h-6 bg-gray-200 border border-gray-400 rounded-sm"></div>
                        </div>
                        <span>Not Visited</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center">
                            <div className="w-8 h-8 bg-red-500" style={{ clipPath: "polygon(0 0, 100% 20%, 100% 80%, 0 100%)" }}></div>
                        </div>
                        <span>Not Answered</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center">
                            <div className="w-8 h-8 bg-green-500" style={{ clipPath: "polygon(0 0, 100% 20%, 100% 80%, 0 100%)" }}></div>
                        </div>
                        <span>Answered</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center">
                            <div className="w-7 h-7 bg-purple-900 rounded-[50%]"></div>
                        </div>
                        <span>Marked for Review</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center">
                            <div className="w-7 h-7 bg-purple-600 rounded-[50%] relative">
                                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white"></div>
                            </div>
                        </div>
                        <span className="leading-tight">Answered &amp; Marked for Review</span>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) return (
        <div className="h-screen flex items-center justify-center font-bold text-xl">
            Loading Test Environment...
        </div>
    );

    const currentQ = questions[currentQIndex];

    return (
        <div className="h-screen mt-20 flex flex-col font-sans bg-white overflow-hidden">

            {/* Top Bar */}
            <div className="bg-orange-500 text-white p-3 flex justify-between items-center shadow-md shrink-0 z-20">
                <h1 className="font-bold text-lg">{quiz?.title || "Mock Test Series"}</h1>
                <div className="font-mono font-bold text-xl bg-white/20 px-3 py-1 rounded">
                    {formatTime(timeLeft)}
                </div>
            </div>

            {/* Main Layout */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden relative">

                {/* Question Area */}
                <div className="flex-1 flex flex-col h-full overflow-hidden relative">

                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                        <img src="/logo.png" alt="" className="w-1/2 md:w-1/3 opacity-5 object-contain" />
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 md:p-8 relative z-10">
                        <div className="max-w-4xl mx-auto">

                            <div className="mb-4">
                                <span className="font-bold text-lg text-gray-800">
                                    Question-{currentQIndex + 1}
                                </span>
                            </div>

                            <div className="mb-8 min-h-[100px]">
                                {currentQ && (
                                    <div
                                        className="text-gray-900 text-lg leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: currentQ.Question }}
                                    />
                                )}
                            </div>

                            {/* Options */}
                            <div className="flex flex-row flex-wrap gap-6 mb-10">
                                {currentQ?.options.map((opt, i) => (
                                    <label key={opt.OptionId} className="flex items-center gap-3 cursor-pointer group min-w-[150px]">
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                            answers[currentQ.QuesId] === opt.OptionId
                                                ? 'border-green-600'
                                                : 'border-gray-400 group-hover:border-gray-600'
                                        }`}>
                                            {answers[currentQ.QuesId] === opt.OptionId && (
                                                <div className="w-3 h-3 bg-green-600 rounded-full" />
                                            )}
                                        </div>
                                        <span className="text-gray-700">({String.fromCharCode(97 + i)})</span>
                                        <span className="text-gray-800" dangerouslySetInnerHTML={{ __html: opt.OptionText }} />
                                        <input
                                            type="radio"
                                            name={`q-${currentQ.QuesId}`}
                                            className="hidden"
                                            onChange={() => handleOptionSelect(currentQ.QuesId, opt.OptionId)}
                                            checked={answers[currentQ.QuesId] === opt.OptionId}
                                        />
                                    </label>
                                ))}
                            </div>

                            {/* Action Buttons Row 1 */}
                            <div className="flex flex-wrap gap-3 mb-6">
                                <button onClick={handleSaveNext} className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-2 rounded shadow-sm">
                                    Save & Next
                                </button>
                                <button onClick={() => handleClear(currentQ.QuesId)} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold px-6 py-2 rounded shadow-sm">
                                    Clear
                                </button>
                                <button onClick={() => handleSaveMarkReview(currentQ.QuesId)} className="bg-orange-400 hover:bg-orange-500 text-white font-semibold px-6 py-2 rounded shadow-sm">
                                    Save & Mark for Review
                                </button>
                                <button onClick={() => handleMarkReviewNext(currentQ.QuesId)} className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 rounded shadow-sm">
                                    Mark for Review & Next
                                </button>
                            </div>

                            <div className="h-px bg-gray-300 w-full mb-6"></div>

                            {/* Nav + Submit Row */}
                            <div className="flex justify-between items-center pb-20 md:pb-0">
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentQIndex(i => Math.max(0, i - 1))}
                                        disabled={currentQIndex === 0}
                                        className="bg-white border border-gray-300 text-gray-800 font-bold px-4 py-2 rounded shadow-sm text-sm hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {`<< BACK`}
                                    </button>
                                    <button
                                        onClick={() => setCurrentQIndex(i => Math.min(questions.length - 1, i + 1))}
                                        disabled={currentQIndex === questions.length - 1}
                                        className="bg-white border border-gray-300 text-gray-800 font-bold px-4 py-2 rounded shadow-sm text-sm hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {`NEXT >>`}
                                    </button>
                                </div>
                                <button onClick={submitTest} className="bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-2 rounded shadow-sm">
                                    SUBMIT
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop Palette */}
                <div className="hidden md:block w-80 bg-white p-4 shrink-0 overflow-hidden h-full z-20 shadow-[-5px_0_15px_rgba(0,0,0,0.05)]">
                    <PaletteContent isMobile={false} />
                </div>
            </div>

            {/* Mobile Palette (bottom strip) */}
            <div className="md:hidden h-auto shrink-0 p-2 bg-white z-20 shadow-[0_-5px_15px_rgba(0,0,0,0.1)]">
                <PaletteContent isMobile={true} />
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
            `}</style>
        </div>
    );
};

export default TestEngineMockPage;